import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEqual, noop } from 'lodash-es';
import classnames from 'classnames';
// Tracking
import { EventToolbox } from 'client/utils/event-toolbox';
import { ACTIONS } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { TrackingConstant } from 'client/tracking/constant';
import { PAGE_EVENTS } from 'client/constants/page-events';
import { EngagementHandler } from 'client/engagement-handlers';
import { InventoryEngagementHandler } from 'client/engagement-handlers/inventory-engagement-handler/inventory-engagement-handler';
// Constants
import { InventoryEntities } from 'client/data/models/inventory';
import { VisitorEntities } from 'client/data/models/visitor';
// Utils
import { scrollToTop } from 'site-modules/personalized-search/utils/scroll-to-top';
import { getPersonalizedSearchPageRoute } from 'site-modules/shared/utils/personalized-search/personalized-search-utils';
import { getQuery } from 'client/utils/location';
import { getFilter, getFilterFromLocation } from 'site-modules/shared/utils/inventory/search-filter';
import { isDealerSRP } from 'site-modules/shared/utils/inventory/srp-type-checkers';
// Hooks
import { useSearchResults } from 'site-modules/personalized-search/hooks/use-search-results';
// Components
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import { InventoryStickyHeader } from 'site-modules/personalized-search/components/inventory/inventory-sticky-header';
import { InventoryCardList } from 'site-modules/personalized-search/components/inventory/inventory-card-list';
import { UsurpInventoryCard } from 'site-modules/shared/components/inventory/usurp-inventory-card/usurp-inventory-card';
import { SrpPagination } from 'site-modules/shared/components/inventory/srp-pagination/srp-pagination';
import { InventoriesNotFoundWithLinks } from 'site-modules/shared/components/inventory/inventories-not-found-with-links/inventories-not-found-with-links';
import { UsurpFiltersDrawer } from 'site-modules/shared/components/inventory/usurp-filters-drawer/usurp-filters-drawer';
import { UsurpFilters } from 'site-modules/shared/components/inventory/usurp-filters/usurp-filters';

import { getFreshHistoryParam } from 'site-modules/shared/components/search-by-module/search-by-module-utils';

import './inventory.scss';

EngagementHandler.register(InventoryEngagementHandler);

export function Inventory({
  facets = [],
  filteringByFacets = [],
  inventories = [],
  radius = null,
  isExpandedRadiusDueToLowResultsSort = false,
  paymentType = undefined,
  selectedPaymentType = undefined,
  pathname = '',
  zipCode = null,
  attributes = {},
  totalPages = 0,
  pageSize = 0,
  pageNumber = 0,
  inventoryCount = 0,
  maxAllowedPageNumber = 0,
  onMultiMMChange = noop,
  onUpdateZipCode = noop,
  isMobile = false,
  selectedFilters = null,
  visitorLocation = null,
  urlContext = {},
  inventoryData = null,
  isLoading = false,
  cardKeyPrefix = undefined,
  onUpdateFiltersFailed = noop,
  onUpdateFilters = noop,
  isMultiMM = false,
  creativeId = undefined,
  listTrackingData = {},
  onRendered = noop,
  location,
}) {
  const containerRef = useRef();
  const selectedFiltersBeforeDrawer = useRef();
  const drawerFiltersCancelled = useRef(false);
  const wasDrawerFacetsChanged = useRef(false);

  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const { searchId, isInventoryFirst, isInventoryOnly } = useSearchResults();

  const onFiltersButtonClick = useCallback(() => {
    selectedFiltersBeforeDrawer.current = selectedFilters;
    setFilterDrawerOpen(true);
  }, [selectedFilters]);

  const closeFiltersDrawer = useCallback(() => {
    setTimeout(() => {
      setFilterDrawerOpen(false);
    }, 0);
    selectedFiltersBeforeDrawer.current = null;
    drawerFiltersCancelled.current = false;
  }, []);

  const cancelFiltersUpdate = useCallback(() => {
    if (!isEqual(selectedFilters, selectedFiltersBeforeDrawer.current)) {
      onUpdateFilters(selectedFiltersBeforeDrawer.current);

      drawerFiltersCancelled.current = true;
      return;
    }
    closeFiltersDrawer();
  }, [closeFiltersDrawer, onUpdateFilters, selectedFilters]);

  const confirmFiltersUpdate = useCallback(() => {
    wasDrawerFacetsChanged.current = selectedFilters !== selectedFiltersBeforeDrawer.current;
    closeFiltersDrawer();
  }, [closeFiltersDrawer, selectedFilters]);

  const onDestroy = useCallback(() => {
    if (wasDrawerFacetsChanged.current) {
      scrollToTop();
      wasDrawerFacetsChanged.current = false;
    }
  }, []);

  const handleFilterUpdate = useCallback(
    (type, facet, isChecked) => {
      const updatedFilter = getFilter({
        type,
        facet,
        isChecked,
        currentFilter: selectedFilters,
        radius,
        isMultiMM,
        facets,
      });

      if (isEqual(updatedFilter, selectedFilters)) {
        return;
      }

      onUpdateFilters(updatedFilter);
    },
    [facets, isMultiMM, onUpdateFilters, radius, selectedFilters]
  );

  const handlePageChange = useCallback(
    e => {
      const { currentTarget, ctrlKey, metaKey } = e;

      if (ctrlKey || metaKey) return;

      e.preventDefault();

      const disabledLink = currentTarget.hasAttribute('disabled');
      if (disabledLink) {
        return;
      }

      scrollToTop(undefined, { behavior: 'instant' });

      const href = currentTarget.getAttribute('href');

      onUpdateFilters(getFilterFromLocation(getQuery({ search: href.split('?')[1] || '' })));
    },
    [onUpdateFilters]
  );

  const handleUpdateZipCode = useCallback(
    async newZipCode => {
      try {
        await onUpdateZipCode(newZipCode);
        onUpdateFilters({ ...selectedFilters, ...getFreshHistoryParam() });
      } catch {
        onUpdateFiltersFailed();
      }
    },
    [onUpdateFilters, onUpdateFiltersFailed, onUpdateZipCode, selectedFilters]
  );

  useEffect(() => {
    onRendered();
  }, [onRendered]);

  useEffect(() => {
    if (drawerFiltersCancelled.current) {
      closeFiltersDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryData]);

  useEffect(() => {
    const fireExpandedRadius = () => {
      const isExpandedRadiusDueToLowResults = !!get(inventoryData, 'attributes.expandedRadiusDueToLowResults', false);
      const attrRadius = get(inventoryData, 'attributes.radius', '').toString();
      if (isExpandedRadiusDueToLowResults) {
        EventToolbox.fireTrackAction({
          event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
          event_data: {
            action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
            action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
            action_name: TrackingConstant.ACTION_SHOW_CONTENT,
            subaction_name: 'expand_search_radius',
            creative_id: 'edm-entry-facets-zip_and_radius',
            value: attrRadius,
          },
        });
      }
    };

    EventToolbox.on(PAGE_EVENTS.PAGE_LOAD, fireExpandedRadius);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (inventoryData) {
      EventToolbox.fireCustomEvent(ACTIONS.INVENTORY_LIST_NEW_FIELDS, listTrackingData);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryData]);

  const handleUpdateFilters = useCallback(
    (event, filters) => {
      event.preventDefault();
      onUpdateFilters(filters);
    },
    [onUpdateFilters]
  );

  if (!inventoryData) return null;

  return (
    <div
      ref={containerRef}
      className={classnames('inventory', { 'mt-2': !isInventoryFirst })}
      data-tracking-parent={creativeId}
    >
      <LoadingSpinner isLoading={isLoading}>
        <div className="container pb-2">
          <InventoryStickyHeader
            inventoryCount={inventoryCount}
            facets={facets}
            filteringByFacets={filteringByFacets}
            onFiltersButtonClick={onFiltersButtonClick}
            onUpdateFilter={handleFilterUpdate}
            isMobile={isMobile}
            ref={containerRef}
            isDealerSrp={isDealerSRP(selectedFilters)}
            dealerName={attributes?.dealerInfo?.name}
            isInventoryOnly={isInventoryOnly}
            selectedFilters={selectedFilters}
            isExpandedRadiusDueToLowResultsSort={isExpandedRadiusDueToLowResultsSort}
          />

          {!!inventoryCount && (
            <InventoryCardList
              inventories={inventories}
              cardComponent={UsurpInventoryCard}
              radius={radius}
              isMobile={isMobile}
              visitorLocation={visitorLocation}
              grayIcons
              paymentType={paymentType}
              selectedPaymentType={selectedPaymentType}
              cardKeyPrefix={cardKeyPrefix}
              onUpdateFilters={onUpdateFilters}
              selectedFilters={selectedFilters}
              location={location}
            />
          )}

          {!inventoryCount && (
            <InventoriesNotFoundWithLinks
              attributes={attributes}
              selectedFacets={selectedFilters}
              inventoriesCount={inventoryCount}
              onUpdate={handleFilterUpdate}
              onZipCodeUpdate={onUpdateZipCode}
              basePathname={getPersonalizedSearchPageRoute()}
              queryParams={{ key: searchId }}
              onUpdateFilters={handleUpdateFilters}
              withLeadForm
            />
          )}

          <SrpPagination
            pageNumber={pageNumber}
            pagesCount={totalPages}
            maxAllowedPageNumber={maxAllowedPageNumber}
            pageSize={pageSize}
            pageResultsCount={inventories.length}
            inventoryCount={inventoryCount}
            selectedFacets={selectedFilters}
            urlContext={urlContext}
            pathName={pathname}
            onPageLinkClick={handlePageChange}
            isMobile={isMobile}
          />
        </div>
      </LoadingSpinner>
      <UsurpFiltersDrawer
        isOpen={isFilterDrawerOpen}
        onToggle={closeFiltersDrawer}
        onCancel={cancelFiltersUpdate}
        onApply={confirmFiltersUpdate}
        onDestroy={onDestroy}
        searchResultsFound={inventoryCount}
        isLoading={isLoading}
        dataTrackingParent="srp-new-drawer-facets"
        isMobile={isMobile}
        filtersComponent={UsurpFilters}
        paymentType={selectedPaymentType}
        facets={facets}
        filteringByFacets={filteringByFacets}
        onUpdate={handleFilterUpdate}
        zipCode={zipCode}
        setIsMultiMM={onMultiMMChange}
        onZipCodeUpdate={handleUpdateZipCode}
        selectedFacets={selectedFilters}
        drawerContainerClasses="bg-cool-gray-10"
        drawerContainerSelector="usurp-filters-drawer-container"
        drawerContentClasses="h-100"
      />
    </div>
  );
}

Inventory.propTypes = {
  inventoryData: InventoryEntities.Inventories,
  facets: InventoryEntities.Facets,
  filteringByFacets: PropTypes.arrayOf(PropTypes.shape({})),
  inventories: InventoryEntities.InventoriesVin,
  radius: PropTypes.number,
  isExpandedRadiusDueToLowResultsSort: PropTypes.bool,
  paymentType: PropTypes.string,
  selectedPaymentType: PropTypes.string,
  pathname: PropTypes.string,
  attributes: InventoryEntities.Attributes,
  totalPages: PropTypes.number,
  pageSize: PropTypes.number,
  pageNumber: PropTypes.number,
  inventoryCount: PropTypes.number,
  maxAllowedPageNumber: PropTypes.number,
  onMultiMMChange: PropTypes.func,
  onUpdateFilters: PropTypes.func,
  onUpdateFiltersFailed: PropTypes.func,
  onUpdateZipCode: PropTypes.func,
  isMobile: PropTypes.bool,
  selectedFilters: PropTypes.shape({}),
  visitorLocation: VisitorEntities.Location,
  urlContext: PropTypes.shape({}),
  zipCode: PropTypes.string,
  isLoading: PropTypes.bool,
  cardKeyPrefix: PropTypes.string,
  isMultiMM: PropTypes.bool,
  creativeId: PropTypes.string,
  listTrackingData: PropTypes.shape({
    inventory: PropTypes.array,
    totalCount: PropTypes.number,
    attributes: PropTypes.shape({}),
    creativeId: PropTypes.string,
    defaults: PropTypes.shape({}),
    isNational: PropTypes.bool,
    radius: PropTypes.number,
  }),
  onRendered: PropTypes.func,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
};
